import * as React from "react";
import { useNavigate } from "react-router";

import { CreateEntityButton } from "@/shared/components/CreateEntityButton";
import { Popover } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";
import { useCollaborationBar } from "@/shared/state/collaborationBar";

import { stashAgentBuilderKickoff } from "./agentBuilderKickoff";
import styles from "./CreateAgentMenu.module.css";

interface CreateOption {
  id: "scratch" | "chat";
  label: string;
  description: string;
}

const OPTIONS: CreateOption[] = [
  {
    id: "scratch",
    label: "Start from scratch",
    description:
      "Open the empty agent editor and fill in name, goals, and kickoff message yourself.",
  },
  {
    id: "chat",
    label: "Build with chat",
    description:
      "Have a conversation with the AI to define the agent, then land in the editor with a draft to review.",
  },
];

interface CreateAgentMenuProps {
  /**
   * `primary` in the agents list's toolbar; `secondary` in its empty state.
   * @default "primary"
   */
  variant?: "primary" | "secondary";
  /**
   * Which edge of the button the panel lines up with. `"end"` grows it
   * leftward, for a button at the right of a toolbar; `"start"` grows it
   * rightward, for a button at the left of its content, beside the sidebar;
   * `"center"` centers it under the button, for the list's empty state.
   */
  align?: "start" | "center" | "end";
}

/**
 * Split create-agent button: one click opens a small popover with two
 * entry-points — the existing empty-form route, and a new "Build with
 * chat" path that hands the user off to the Agent Chat sidebar with a
 * curated kickoff message.
 *
 * Does NOT claim the WAI-ARIA `menu` role: that role advertises
 * Escape/Arrow/Home/End/type-ahead keyboard semantics, none of which we
 * implement. The popover is a plain click-to-show panel with two buttons.
 * It is portalled and kept inside the viewport, so no ancestor's overflow
 * or the sidebar can cover it.
 */
export function CreateAgentMenu({
  variant = "primary",
  align = "end",
}: CreateAgentMenuProps) {
  const navigate = useNavigate();
  const collaborationBar = useCollaborationBar();
  const [open, setOpen] = React.useState(false);

  const handleOptionClick = (option: CreateOption) => {
    setOpen(false);
    if (option.id === "scratch") {
      void navigate(Goto.route.CreateAgent);
      return;
    }
    // "chat": stash the curated kickoff and point the Agent Chat sidebar at a
    // new thread. The kickoff is a "start over with this prompt" signal, so it
    // names the address rather than landing on whatever thread the bar was
    // left on. The sidebar's AgentThread component picks the message up via
    // either its mount-time sessionStorage read (when the bar was closed) or
    // the window event (when the bar is already open).
    stashAgentBuilderKickoff();
    collaborationBar.open({
      mode: "ai-agent-chat",
      threadId: null,
      view: "thread",
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        render={<CreateEntityButton entity="agent" variant={variant} />}
      />
      <Popover.Content
        align={align}
        aria-label="Create an agent"
        className={styles.popover}
      >
        {OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            className={styles.option}
            onClick={() => handleOptionClick(option)}
          >
            <span className={styles.optionLabel}>{option.label}</span>
            <span className={styles.optionDescription}>
              {option.description}
            </span>
          </button>
        ))}
      </Popover.Content>
    </Popover>
  );
}
